import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL } from './lib/api-config';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';
import PreCourseReport from './components/reports/PreCourseReport';
import './Reports.css';

function Reports() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('sales'); // 'sales' | 'insights' | 'preCourse'

  // Date range for sales report
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  // Sales report data
  const [salesData, setSalesData] = useState(null);

  // Insights data
  const [insights, setInsights] = useState(null);

  const config = { withCredentials: true };

  useEffect(() => {
    if (activeTab === 'sales') {
      fetchSalesReport();
    } else if (activeTab === 'insights') {
      fetchInsights();
    } else {
      // preCourse tab manages its own loading
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchSalesReport = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(
        `${API_URL}/reports/sales?from=${dateRange.from}&to=${dateRange.to}`,
        config
      );
      setSalesData(response.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Fel vid hämtning av säljrapport:', err);
      setError('Kunde inte hämta säljrapport');
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_URL}/deals/insights`, config);
      setInsights(response.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Fel vid hämtning av insikter:', err);
      setError('Kunde inte hämta insikter');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return '-';
    const days = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading && activeTab !== 'preCourse') {
    return (
      <main className="main-content">
        <LoadingSpinner message="Laddar rapporter..." />
      </main>
    );
  }

  return (
    <main className="main-content reports-page">
      <div className="page-header">
        <h2>Rapporter & Analys</h2>
      </div>

      <AlertMessage error={error} />

      {/* Tabs */}
      <div className="report-tabs">
        <button
          className={`tab ${activeTab === 'sales' ? 'active' : ''}`}
          onClick={() => setActiveTab('sales')}
        >
          Säljrapport
        </button>
        <button
          className={`tab ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          Pipeline-insikter
        </button>
        <button
          className={`tab ${activeTab === 'preCourse' ? 'active' : ''}`}
          onClick={() => setActiveTab('preCourse')}
        >
          Förkurs-rapport
        </button>
      </div>

      {activeTab === 'preCourse' && <PreCourseReport />}

      {/* Sales Report */}
      {activeTab === 'sales' && (
        <div className="report-content">
          <div className="date-filter">
            <label>
              Från:
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              />
            </label>
            <label>
              Till:
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              />
            </label>
            <button className="btn btn-primary" onClick={fetchSalesReport}>
              Uppdatera
            </button>
          </div>

          {salesData && (
            <>
              {/* KPI Cards */}
              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="kpi-value">{salesData.summary.wonDealsCount}</div>
                  <div className="kpi-label">Vunna affärer</div>
                </div>
                <div className="kpi-card highlight">
                  <div className="kpi-value">{formatCurrency(salesData.summary.totalRevenue)}</div>
                  <div className="kpi-label">Total intäkt</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{formatCurrency(salesData.summary.avgDealValue)}</div>
                  <div className="kpi-label">Snitt per affär</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{salesData.summary.winRate}%</div>
                  <div className="kpi-label">Win rate</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{salesData.summary.avgVelocityDays} dagar</div>
                  <div className="kpi-label">Snitt cykeltid</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value">{salesData.summary.createdDealsCount}</div>
                  <div className="kpi-label">Nya leads</div>
                </div>
              </div>

              {/* Top Performers */}
              {salesData.topPerformers.length > 0 && (
                <div className="report-section">
                  <h3>Toppsäljare</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Säljare</th>
                        <th>Vunna</th>
                        <th>Intäkt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesData.topPerformers.map((p, i) => (
                        <tr key={i}>
                          <td>{p.name}</td>
                          <td>{p.deals}</td>
                          <td>{formatCurrency(p.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Won Deals List */}
              {salesData.wonDeals.length > 0 && (
                <div className="report-section">
                  <h3>Vunna affärer</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Affär</th>
                        <th>Värde</th>
                        <th>Stängd</th>
                        <th>Säljare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesData.wonDeals.map(deal => (
                        <tr key={deal.id}>
                          <td>{deal.name}</td>
                          <td>{formatCurrency(deal.value)}</td>
                          <td>{formatDate(deal.wonAt)}</td>
                          <td>{deal.assignedTo || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Pipeline Insights */}
      {activeTab === 'insights' && insights && (
        <div className="report-content">
          <div className="insights-grid">
            {/* Needs Attention */}
            <div className="insight-card warning">
              <h3>Behöver uppmärksamhet</h3>
              <p className="insight-desc">Ingen aktivitet senaste 7 dagarna</p>
              {insights.needsAttention.length === 0 ? (
                <p className="no-data">Inga deals behöver uppmärksamhet just nu</p>
              ) : (
                <ul className="insight-list">
                  {insights.needsAttention.map(deal => (
                    <li key={deal.id}>
                      <div className="insight-deal">
                        <span className="deal-name">{deal.name}</span>
                        <span className="deal-value">{formatCurrency(deal.value)}</span>
                      </div>
                      <div className="insight-meta">
                        {deal.company && <span>{deal.company}</span>}
                        <span className="days-inactive">{daysSince(deal.lastActivityAt)} dagar sedan</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Closing Soon */}
            <div className="insight-card info">
              <h3>Stängs snart</h3>
              <p className="insight-desc">Förväntat avslut inom 7 dagar</p>
              {insights.closingSoon.length === 0 ? (
                <p className="no-data">Inga deals stängs inom 7 dagar</p>
              ) : (
                <ul className="insight-list">
                  {insights.closingSoon.map(deal => (
                    <li key={deal.id}>
                      <div className="insight-deal">
                        <span className="deal-name">{deal.name}</span>
                        <span className="deal-value">{formatCurrency(deal.value)}</span>
                      </div>
                      <div className="insight-meta">
                        <span className="probability">{deal.probability}%</span>
                        <span className="close-date">Stängs {formatDate(deal.expectedCloseDate)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* At Risk */}
            <div className="insight-card danger">
              <h3>Risk att förlora</h3>
              <p className="insight-desc">Låg sannolikhet, högt värde</p>
              {insights.atRisk.length === 0 ? (
                <p className="no-data">Inga deals identifierade som riskfyllda</p>
              ) : (
                <ul className="insight-list">
                  {insights.atRisk.map(deal => (
                    <li key={deal.id}>
                      <div className="insight-deal">
                        <span className="deal-name">{deal.name}</span>
                        <span className="deal-value">{formatCurrency(deal.value)}</span>
                      </div>
                      <div className="insight-meta">
                        <span className="probability low">{deal.probability}%</span>
                        {deal.stage && (
                          <span className="stage" style={{ backgroundColor: deal.stage.color }}>
                            {deal.stage.name}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Hot Opportunities */}
            <div className="insight-card success">
              <h3>Heta möjligheter</h3>
              <p className="insight-desc">Hög sannolikhet, högt värde</p>
              {insights.hotOpportunities.length === 0 ? (
                <p className="no-data">Inga heta möjligheter just nu</p>
              ) : (
                <ul className="insight-list">
                  {insights.hotOpportunities.map(deal => (
                    <li key={deal.id}>
                      <div className="insight-deal">
                        <span className="deal-name">{deal.name}</span>
                        <span className="deal-value">{formatCurrency(deal.value)}</span>
                      </div>
                      <div className="insight-meta">
                        <span className="probability high">{deal.probability}%</span>
                        {deal.assignedTo && <span>{deal.assignedTo}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Reports;
